import { useState } from "react";
import { useTranslation } from "react-i18next";

import { resolveMapLink } from "../services/branchService";
import { coordinatesFrom } from "../services/mapLink";

/**
 * Arabic-Indic (٠-٩) and Eastern Arabic-Indic (۰-۹) digits, folded to ASCII.
 *
 * The API's regexes are `[0-9]` only, so a number typed on an Arabic keyboard
 * would be refused on save. Somebody who typed digits *has* entered a number,
 * and deleting it in front of them would look like the field was broken — so it
 * is translated rather than stripped.
 */
const toAsciiDigits = (raw) =>
  raw
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0));

/**
 * Digits only, which is what `postal_code` accepts.
 *
 * The hint under the field has always said "digits only" while the input took
 * anything, so a letter was only refused on save — after a round trip, against
 * a field that had promised otherwise. Capped at the API's 12.
 */
const digitsOnly = (raw) => toAsciiDigits(raw).replace(/\D/g, "").slice(0, 12);

/**
 * Digits with one optional leading `+`, which is what `phone` accepts.
 *
 * The `+` is kept only where E.164 puts it — at the front — so a stray one
 * typed mid-number is dropped rather than making the whole value invalid.
 * Letters are removed, not rejected with a message: the rule is simple enough
 * that showing it happening is clearer than explaining it. Capped at the API's
 * 20 digits, with the `+` on top of that.
 */
const phoneDigits = (raw) => {
  const ascii = toAsciiDigits(raw);
  const plus = ascii.trimStart().startsWith("+") ? "+" : "";

  return plus + ascii.replace(/\D/g, "").slice(0, 20);
};

/**
 * The location half of a branch: how to reach it, where exactly it is, how much
 * it can take at once, and whether it is open for business.
 *
 * Shared by the create page and the location settings modal so the two never
 * drift. City, currency and business types stay with their pickers on the create
 * page — those are lookups, not plain fields.
 */
export default function BranchLocationFields({
  values,
  onChange,
  errors = {},
  timezones = [],
  // The plan's active-branch limit is reached and this branch is not already
  // active: "Active" stays visible but cannot be picked. The API refuses it
  // anyway (422 on `status`); this only says so before the round trip.
  activeLockedHint = "",
  // Create demands the map link, because a new branch has no other source of
  // coordinates. Edit does not: a branch that predates the rule keeps whatever
  // location it already has.
  required = false,
}) {
  const { t } = useTranslation();

  // `sanitize` rewrites what the user typed before it is stored, so a field
  // that cannot hold a character never holds one — see `digitsOnly` below.
  const set = (field, sanitize) => (e) =>
    onChange({ ...values, [field]: sanitize ? sanitize(e.target.value) : e.target.value });

  // Which layer filled the coordinates, so the hint can say. Null when nothing
  // was filled — a link the user typed over, or one nothing could read.
  const [filledFrom, setFilledFrom] = useState(null);
  const [resolving, setResolving] = useState(false);

  /**
   * Pasting a Maps link fills the coordinate boxes when they are empty.
   *
   * OUP-5 made the coordinates required and validation runs before the
   * controller, so the API can no longer derive them itself — a blank pair is
   * refused outright. Doing it here means the required fields are satisfied by
   * the link the user already pasted, and what they typed is never overwritten.
   */
  const coordinatesAreEmpty = (v) =>
    !String(v.latitude ?? "").trim() && !String(v.longitude ?? "").trim();

  /**
   * Typing the link tries the free layer immediately — the coordinates are
   * usually already in a full Maps URL, and reading them here costs nothing and
   * shows up as you type.
   */
  const setMapUrl = (e) => {
    const mapUrl = e.target.value;
    const next = { ...values, mapUrl };

    if (coordinatesAreEmpty(values)) {
      const derived = coordinatesFrom(mapUrl);
      if (derived) {
        next.latitude = derived.latitude;
        next.longitude = derived.longitude;
        setFilledFrom("regex");
      }
    }

    onChange(next);
  };

  /**
   * On blur, ask the server for the links the browser cannot read: a short
   * maps.app.goo.gl needs its redirect followed, which a page cannot do
   * cross-origin. Only when the free layer already failed and both coordinate
   * boxes are still empty — what the user typed is never overwritten, and a
   * link that resolved locally is not re-resolved on the server's time.
   *
   * A failure is silent by design. There is no coordinate field to fall back
   * to any more, so an error here would be telling somebody off for pasting a
   * link that happens to be short, with nothing they could do about it. The
   * server tries the whole cascade again on save; if that fails too, the branch
   * is stored without coordinates and simply does not appear in nearby
   * searches until its link is replaced with one that resolves.
   */
  const resolveOnBlur = async () => {
    const mapUrl = String(values.mapUrl ?? "").trim();

    if (mapUrl === "" || !coordinatesAreEmpty(values) || coordinatesFrom(mapUrl)) return;

    setResolving(true);
    try {
      const found = await resolveMapLink(mapUrl);
      if (found?.success && found.lat != null && found.lng != null) {
        onChange({
          ...values,
          latitude: String(found.lat),
          longitude: String(found.lng),
        });
        setFilledFrom(found.source ?? "redirect");
      }
    } catch {
      // Nothing to say, and nowhere to say it. The save re-runs the cascade.
    } finally {
      setResolving(false);
    }
  };
  const fieldError = (field) => errors[field]?.[0];

  // `errorKey` is for the fields whose API name is not their form name —
  // mapUrl is reported as map_url.
  const text = (
    field,
    { label, type = "text", dir, hint, span, errorKey, inputMode, sanitize, maxLength } = {},
  ) => {
    const error = fieldError(errorKey ?? field);

    return (
      <div className={span ? "md:col-span-2" : undefined}>
        <label className="field-label" htmlFor={field}>
          {label ?? t(`branchForm.${field}`)}
        </label>
        <input
          id={field}
          type={type}
          dir={dir}
          inputMode={inputMode}
          maxLength={maxLength}
          aria-invalid={error ? true : undefined}
          className={`field-input ${error ? "border-danger" : ""}`}
          value={values[field] ?? ""}
          onChange={set(field, sanitize)}
        />
        {hint && <p className="mt-1 text-xs text-ink-2">{hint}</p>}
        {error && <p className="field-error">{error}</p>}
      </div>
    );
  };

  return (
    <>
      {text("code", { hint: t("branchForm.codeHint") })}

      <div>
        <label className="field-label" htmlFor="status">
          {t("branchForm.status")}
        </label>
        <select id="status" className="field-input" value={values.status ?? "active"} onChange={set("status")}>
          {/* draft while it is being set up, closed when shut for now. */}
          {["draft", "active", "closed"].map((status) => (
            <option key={status} value={status} disabled={status === "active" && Boolean(activeLockedHint)}>
              {t(`branchForm.statuses.${status}`)}
            </option>
          ))}
        </select>
        {activeLockedHint && !fieldError("status") && (
          <p className="mt-1 text-xs text-ink-2">{activeLockedHint}</p>
        )}
        {fieldError("status") && <p className="field-error">{fieldError("status")}</p>}
      </div>

      {text("address", { label: `${t("branchForm.address")} *`, span: true })}
      {/* The API reports these in snake_case; without errorKey their 422s never
          reached the input and a refused save looked like nothing happening. */}
      {text("addressLine2", { label: t("branchForm.addressLine2"), span: true, errorKey: "address_line2" })}
      {text("region", { label: t("branchForm.region") })}
      {/* `inputMode` only picks the on-screen keyboard — it refuses nothing, and
          on a desktop browser it refuses nothing at all. `sanitize` is what
          actually holds these two to the digits their hints promise. */}
      {text("postalCode", {
        label: t("branchForm.postalCode"),
        dir: "ltr",
        errorKey: "postal_code",
        inputMode: "numeric",
        hint: t("branchForm.postalCodeHint"),
        sanitize: digitsOnly,
        maxLength: 12,
      })}
      {text("phone", {
        label: t("branchForm.phone"),
        dir: "ltr",
        inputMode: "tel",
        hint: t("branchForm.phoneHint"),
        sanitize: phoneDigits,
        maxLength: 21,
      })}
      {text("email", { label: t("branchForm.email"), type: "email", dir: "ltr" })}

      <div>
        <label className="field-label" htmlFor="capacity">
          {t("branchForm.capacity")}
        </label>
        <input
          id="capacity"
          type="number"
          min="1"
          dir="ltr"
          className="field-input"
          value={values.capacity ?? ""}
          onChange={set("capacity")}
        />
        <p className="mt-1 text-xs text-ink-2">{t("branchForm.capacityHint")}</p>
        {fieldError("capacity") && <p className="field-error">{fieldError("capacity")}</p>}
      </div>

      <div>
        <label className="field-label" htmlFor="timezone">
          {t("branchForm.timezone")}
        </label>
        {/* Left blank, the API falls back to the city's country timezone. */}
        <input
          id="timezone"
          list="branch-timezones"
          dir="ltr"
          className="field-input"
          placeholder={t("branchForm.timezonePlaceholder")}
          value={values.timezone ?? ""}
          onChange={set("timezone")}
        />
        <datalist id="branch-timezones">
          {timezones.map((zone) => (
            <option key={zone} value={zone} />
          ))}
        </datalist>
        <p className="mt-1 text-xs text-ink-2">{t("branchForm.timezoneHint")}</p>
        {fieldError("timezone") && <p className="field-error">{fieldError("timezone")}</p>}
      </div>

      <div className="md:col-span-2">
        <label className="field-label" htmlFor="description">
          {t("branchForm.description")}
        </label>
        <textarea
          id="description"
          rows={3}
          className="field-input"
          value={values.description ?? ""}
          onChange={set("description")}
        />
        {fieldError("description") && <p className="field-error">{fieldError("description")}</p>}
      </div>

      {/*
        Latitude and longitude used to sit here, starred and typed by hand. They
        are read from this link instead: nobody has two decimal numbers to hand
        and everybody has the link. They are still stored, still what distance
        search runs on, and still submitted with the form — they are just no
        longer somebody's job.

        The link is required when creating a branch, because it is now the only
        thing a new branch's location can come from.
      */}
      <div className="md:col-span-2">
        <label className="field-label" htmlFor="mapUrl">
          {t("branchForm.mapUrl")} {required && <span className="text-danger-text">*</span>}
        </label>
        <input
          id="mapUrl"
          type="url"
          dir="ltr"
          className={`field-input ${fieldError("map_url") ? "border-danger" : ""}`}
          value={values.mapUrl ?? ""}
          onChange={setMapUrl}
          onBlur={resolveOnBlur}
        />
        <p className="mt-1 text-xs text-ink-2">{t("branchForm.mapUrlHint")}</p>
        {resolving && (
          <p className="mt-1 text-xs text-ink-2">{t("branchForm.mapUrlResolving")}</p>
        )}
        {!resolving && filledFrom && (
          <p className="mt-1 text-xs text-accent-text">
            {t("branchForm.mapUrlFilled", { source: t(`branchForm.mapUrlSources.${filledFrom}`) })}
          </p>
        )}
        {fieldError("map_url") && <p className="field-error">{fieldError("map_url")}</p>}
      </div>
    </>
  );
}
